import { SyncOutlined } from '@ant-design/icons';
import { ConfigProvider, Button, Form, Input, message, Modal, Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Province } from "@/core/models/address/province.model";
import { District } from "@/core/models/address/district.model";
import { Ward } from "@/core/models/address/ward.model";
import container from "@/lib/di/container";
import { AddressRepo } from "@/core/repos/address.repo";
import { UserDetail } from "@/core/models/auth/auth.model";
import { UserRepo } from "@/core/repos/user.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import { useDrafts } from '@/core/stores/drafts.store';
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';

const addressRepo = container.get(AddressRepo);
const userRepo = container.get(UserRepo);
const tollAreaRepo = container.get(TollAreaRepo);

interface CreateModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: TollAreaModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<CreateModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<TollAreaModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);
    const [wards, setWards] = useState<Ward[]>([]);
    const [users, setUsers] = useState<UserDetail[]>([]);
    const [messageApi, contextHolder] = message.useMessage();
    // Draft logic for TollAreaModel
    const draft = useDrafts((state) => state.getDraft('TollAreaModel'));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft('TollAreaModel', form.getFieldsValue());
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft('TollAreaModel');
        form.setFieldsValue(data);
    };
    //Attach hooks to handle data changes
    const handleDataChange = async () => {
        await handleProvinceChange(data.provinceId);
        await handleDistrictChange(data.districtId);
        form.setFieldsValue(data);
        if (data.id&&data.id.length>0) {
            setIsEdit(true);
        } else {
            if (draft) form.setFieldsValue(draft);
            setIsEdit(false);
        }
    }
    useEffect(()=>{
        handleDataChange();
    }, [data])

    //Initialize select options data
    const populateProvinces = async () => {
        const _provinces = (await addressRepo.getAllProvinces()).map(data => data[1]);
        setProvinces(_provinces);
    }
    const populateFactoryUsers = async () => {
        const _users = (await userRepo.getFromFactory());
        setUsers(_users);
    }
    const init = async () => {
        await Promise.all([
            populateProvinces(),
            populateFactoryUsers(),
        ])
    }
    //componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const handleProvinceChange = async (provinceId: string) => {
        const _districts = (await addressRepo.getAllDistricts(provinceId)).map(data => data[1]);
        setDistricts(_districts);
        setWards([]);
        form.setFieldValue("districtId", "");
        form.setFieldValue("wardId", "");
    }

    const handleDistrictChange = async (districtId: string) => {
        const _wards = (await addressRepo.getAllWards(districtId)).map(data => data[1]);
        setWards(_wards);
        form.setFieldValue("wardId", "");
    }
  
    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                const result = await tollAreaRepo.update({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật Khu vực"
                    })
                } else {
                    messageApi.error({
                        content: "Cập nhật khu vực thất bại"
                    })
                }
            } else {
                const result = await tollAreaRepo.create({...data, ...formData});
                setIsModalOpen(false);
                clearDraft();
                if (result) {
                    messageApi.success({
                        content: "Thêm Khu vực mới thành công"
                    })
                } else {
                    messageApi.error({
                        content: "Thêm khu vực mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    
    const handleCancel = () => {
      if (!isEdit) saveDraft();
      setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal open={isModalOpen} onCancel={handleCancel}  width={980} centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
            ]}>
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT KHU VỰC</p>
                    {!isEdit && <ResetDraftButton onClick={clearDraft} />}
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<TollAreaModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit&&!permissionStore.page.edit}
                    >
                        <div className="grid grid-cols-2 gap-x-4 items-end">
                            <Form.Item<TollAreaModel>
                                label="Tên"
                                name="name"
                                rules={[{ required: true, message: 'Vui lòng nhập tên khu vực!' }]}
                            >
                                <Input placeholder="Nhập tên" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="Mã"
                                name="code"
                                rules={[{ required: true, message: 'Mã không được để trống' }]}
                            >
                                <Input placeholder="Nhập mã" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="Tỉnh/ TP"
                                name="provinceId"
                                rules={[{ required: true, message: 'Bạn phải chọn Tỉnh/ TP!' }]}
                            >
                                <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} onChange={handleProvinceChange}>
                                    {provinces.map(province => <Select.Option key={province.id} value={province.id}>{province.name}</Select.Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="Quản lý"
                                name="managerId"
                                rules={[{ required: true, message: 'Vui lòng chọn Quản lý!' }]}
                            >
                                <Select placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                    {users.map(user => <Select.Option key={user.id} value={user.id}>{user.fullName} - {user.userName.toUpperCase()}</Select.Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="Quận/ Huyện"
                                name="districtId"
                                rules={[{ required: true, message: 'Bạn phải chọn Quận/ Huyện!' }]}
                            >
                                <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} onChange={handleDistrictChange}>
                                    {districts.map(district => <Select.Option key={district.id} value={district.id}>{district.name}</Select.Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="Người đọc số"
                                name="readerId"
                                rules={[{ required: true, message: 'Vui lòng chọn Người đọc số!' }]}
                            >
                                <Select placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                    {users.map(user => <Select.Option key={user.id} value={user.id}>{user.fullName} - {user.userName.toUpperCase()}</Select.Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="Phường/ Xã"
                                name="wardId"
                                rules={[{ required: true, message: 'Bạn phải chọn Phường/ Xã!' }]}
                            >
                                <Select placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                    {wards.map(ward => <Select.Option key={ward.id} value={ward.id}>{ward.name}</Select.Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="Người thu tiền"
                                name="tollCollectorId"
                                rules={[{ required: true, message: 'Vui lòng chọn Người thu tiền!' }]}
                            >
                                <Select placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                    {users.map(user => <Select.Option key={user.id} value={user.id}>{user.fullName} - {user.userName.toUpperCase()}</Select.Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="Ngày chốt sổ"
                                name="exportDate"
                            >
                                <Input placeholder="Nhập ngày chốt sổ" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<TollAreaModel>
                                label="GPS"
                                name="gps"
                            >
                                <Input placeholder="Nhập toạ độ GPS" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}